import { useEffect, useMemo, useState } from "react";
import { feConversionMonthly, listIncentiveLocks, lockIncentiveMonth, unlockIncentiveMonth } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, IndianRupee, TrendingUp, Users, ChevronDown, ChevronUp, Lock, Unlock } from "lucide-react";
import { numberLocale } from "@/lib/money";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtSlabRange = (mn, mx) => {
  const fmt = (v) => (v >= 100000 ? `${(v / 100000).toFixed(v % 100000 ? 1 : 0)}L` : `${(v / 1000).toFixed(0)}K`);
  // Treat null/undefined OR a sentinel "very large" value (e.g. 10,000,000) as "no upper bound"
  const noCap = mx === null || mx === undefined || mx >= 10_000_000;
  if (noCap) return `${fmt(mn)}+`;
  return `${fmt(mn)} – ${fmt(mx)}`;
};

export default function FEConversionsPage() {
  const { user } = useAuth();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [locks, setLocks] = useState([]);
  const [locking, setLocking] = useState(false);

  const canLock = atLeast(user?.role, "manager");
  const canUnlock = atLeast(user?.role, "admin");
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const currentLock = locks.find((l) => l.month === monthKey) || null;

  const refreshLocks = () => listIncentiveLocks().then(setLocks).catch(() => {});
  useEffect(() => { feConversionMonthly(year, month).then(setData); }, [year, month]);
  useEffect(() => { refreshLocks(); }, []);

  const shift = (n) => {
    let y = year, m = month + n;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const doLock = async () => {
    if (!window.confirm(`Confirm & lock ${MONTH_NAMES[month - 1]} ${year}? After locking, only admins can change payment dates or programme-start dates in this month.`)) return;
    setLocking(true);
    try {
      await lockIncentiveMonth(monthKey);
      toast.success(`Locked ${MONTH_NAMES[month - 1]} ${year}`);
      refreshLocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to lock month");
    } finally {
      setLocking(false);
    }
  };

  const doUnlock = async () => {
    if (!window.confirm(`Unlock ${MONTH_NAMES[month - 1]} ${year}? Users will be able to edit dates in this month again.`)) return;
    setLocking(true);
    try {
      await unlockIncentiveMonth(monthKey);
      toast.success(`Unlocked ${MONTH_NAMES[month - 1]} ${year}`);
      refreshLocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock month");
    } finally {
      setLocking(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="fe-conversions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Front End Challenge · Monthly incentive</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Conversions Dashboard</h2>
          <p className="text-sm text-zinc-400 mt-1">
            FE Retention (previously FE Conversion) is a front-end member (6 Week Challenge) moving onto a long-term membership (12 Week, 24 Week, Annual),
            entered in <span className="text-zinc-300">All Sales</span> with status <span className="text-zinc-300">FE Retention</span>.
            Paid to the <span className="text-zinc-300">Accountability Coach</span> on that sale. Base = <span className="text-zinc-300">net received</span>
            (after GST and payment fees) in the month the money came in. The whole amount is paid at the rate of the slab
            it falls in (slabs below), e.g. ₹77,857 in 60K–1L earns 6% of ₹77,857. New Consult sales don&apos;t count.
          </p>
          {currentLock && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-200" data-testid="fec-lock-badge">
              <Lock className="h-3 w-3" />
              Locked on {new Date(currentLock.locked_at).toLocaleDateString()} by {currentLock.locked_by_name || currentLock.locked_by_email || "—"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canLock && !currentLock && (
            <button
              onClick={doLock}
              disabled={locking}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-xs px-3 py-1.5 font-medium transition-colors disabled:opacity-50"
              data-testid="fec-lock-month-btn"
            >
              <Lock className="h-3.5 w-3.5" /> Confirm & Lock {MONTH_NAMES[month - 1]} {year}
            </button>
          )}
          {currentLock && canUnlock && (
            <button
              onClick={doUnlock}
              disabled={locking}
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 text-xs px-3 py-1.5 font-medium transition-colors disabled:opacity-50"
              data-testid="fec-unlock-month-btn"
            >
              <Unlock className="h-3.5 w-3.5" /> Unlock {MONTH_NAMES[month - 1]} {year}
            </button>
          )}
          {currentLock && !canUnlock && (
            <div className="text-xs text-zinc-500" data-testid="fec-unlock-forbidden">Only admins can unlock this month.</div>
          )}
          <div className="inline-flex items-center gap-1 rounded-full border border-[#262626] bg-[#0a0a0a] px-1 py-1" data-testid="fec-month-picker">
            <button onClick={() => shift(-1)} className="p-1.5 rounded-full hover:bg-[#1a1a1a]" data-testid="fec-prev-month" aria-label="Previous month"><ChevronLeft className="h-4 w-4 text-zinc-400" /></button>
            <div className="text-sm text-zinc-100 min-w-[7rem] text-center font-medium" data-testid="fec-month-label">
              {currentLock && <Lock className="inline-block h-3 w-3 text-amber-300 mr-1 -mt-0.5" />}
              {MONTH_NAMES[month - 1]} {year}
            </div>
            <button onClick={() => shift(1)} className="p-1.5 rounded-full hover:bg-[#1a1a1a]" data-testid="fec-next-month" aria-label="Next month"><ChevronRight className="h-4 w-4 text-zinc-400" /></button>
          </div>
        </div>
      </div>

      {/* Slab structure card */}
      {data && (
        <section className="card-surface p-5" data-testid="fec-slabs">
          <div className="eyebrow mb-2">Incentive structure</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {(data.slabs || []).map((s) => (
              <div key={`${s.min}-${s.max ?? "inf"}-${s.rate}`} className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 text-center">
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80">{fmtSlabRange(s.min, s.max)}</div>
                <div className="text-2xl font-bold text-emerald-300 tabular-nums mt-1">{(s.rate * 100).toFixed(0)}%</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* KPI row */}
      {data && (
        <section>
          <div className="eyebrow mb-3">Month at a glance</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Kpi label="Conversions" value={data.total_conversions} icon={Users} testid="fec-total-conv" />
            <Kpi label="Net Revenue" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(data.total_revenue)}`} icon={IndianRupee} tint="emerald" testid="fec-total-rev" />
            <Kpi label="Incentive payout" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(data.total_payout)}`} icon={TrendingUp} tint="amber" testid="fec-total-payout" />
          </div>
        </section>
      )}

      {/* Per-rep table */}
      <section className="card-surface p-6" data-testid="fec-reps-section">
        <div className="eyebrow">Rep breakdown</div>
        <h3 className="font-display text-xl font-bold mt-1 mb-4">Accountability Coaches · {MONTH_NAMES[month - 1]} {year}</h3>
        {!data ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : data.reps.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">No FE Retention sales logged for this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fec-reps-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-3 py-2 font-medium">&nbsp;</th>
                  <th className="px-3 py-2 font-medium">Accountability Coach</th>
                  <th className="px-3 py-2 font-medium text-right">Conversions</th>
                  <th className="px-3 py-2 font-medium text-right">Net revenue</th>
                  <th className="px-3 py-2 font-medium text-right" title="The slab the month's net received falls in">Slab rate</th>
                  <th className="px-3 py-2 font-medium text-right">Payout</th>
                </tr>
              </thead>
              <tbody>
                {(data.reps || []).map((r) => {
                  const isOpen = !!expanded[r.name];
                  return (
                    <>
                      <tr key={r.name} className="border-b border-[#1a1a1a] hover:bg-[#161616]" data-testid={`fec-rep-${r.name}`}>
                        <td className="px-3 py-2.5">
                          <button onClick={() => setExpanded((s) => ({ ...s, [r.name]: !s[r.name] }))} className="p-1 rounded hover:bg-[#1a1a1a]" aria-label="Toggle" data-testid={`fec-toggle-${r.name}`}>
                            {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 font-medium">{r.name}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">{r.conversions}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.revenue)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{(r.slab_rate * 100).toFixed(1)}%</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-300 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.payout)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                          <td></td>
                          <td colSpan={5} className="px-3 py-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Included sales</div>
                            <div className="space-y-1">
                              {(r.members || []).map((m) => (
                                <div key={m.id} className="flex items-center justify-between text-xs bg-[#111] rounded-lg px-3 py-1.5 border border-[#1a1a1a]">
                                  <div>
                                    <span className="text-zinc-100 font-medium">{m.name || "—"}</span>
                                    <span className="text-zinc-500 ml-2">· {new Date(m.date).toLocaleDateString()}</span>
                                    {m.programme && <span className="text-zinc-500 ml-2">· {m.programme}</span>}
                                    {m.sales_rep && <span className="text-zinc-500 ml-2">· sold by <span className="text-zinc-400">{m.sales_rep}</span></span>}
                                  </div>
                                  <div className="font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(m.net_received)}</div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint, testid }) {
  const tints = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300", blue: "text-blue-300" };
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${tints[tint] || "text-zinc-500"}`} />}
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums mt-2 ${tints[tint] || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
